/**
 * Shared Excel export utilities for reports and timesheets.
 *
 * Provides consistent styling that mirrors the web UI appearance:
 * - monday.com color palette
 * - Utilization heat-map colors
 * - Status badge colors
 * - Deviation colors
 */

import ExcelJS from 'exceljs';
import { downloadBlob } from './download';

// ─── Color helpers ──────────────────────────────────────────────────────────

/** Normalize a hex color: expand 3-char shorthand and strip # */
function normalizeHex(hex: string): string {
  const c = hex.replace('#', '');
  if (c.length === 3) {
    return `${c[0]}${c[0]}${c[1]}${c[1]}${c[2]}${c[2]}`.toUpperCase();
  }
  return c.toUpperCase().padStart(6, '0');
}

/** Convert "#579bfc" → "FF579BFC" (ExcelJS ARGB format) */
export function hexToArgb(hex: string): string {
  return `FF${normalizeHex(hex)}`;
}

/** Lighten a hex color for softer backgrounds */
export function lightenHex(hex: string, factor = 0.65): string {
  const c = normalizeHex(hex);
  const r = Math.round(parseInt(c.substring(0, 2), 16) + (255 - parseInt(c.substring(0, 2), 16)) * factor);
  const g = Math.round(parseInt(c.substring(2, 4), 16) + (255 - parseInt(c.substring(2, 4), 16)) * factor);
  const b = Math.round(parseInt(c.substring(4, 6), 16) + (255 - parseInt(c.substring(4, 6), 16)) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Format a date string as DD/MM/YYYY to match the web UI */
export function formatDateForExcel(dateStr: string | null): string {
  if (!dateStr) return '\u2013';
  const [y, m, d] = dateStr.split('-');
  if (!y || !m || !d) return dateStr;
  return `${d}/${m}/${y}`;
}

/**
 * Round a number to `dp` decimal places using the same logic the web UI uses.
 * Prevents floating-point drift (e.g. 12.7999… → 13 in Excel vs 12.80 on web).
 */
export function round(value: number, dp = 2): number {
  const factor = Math.pow(10, dp);
  return Math.round(value * factor) / factor;
}

// ─── Shared style constants ─────────────────────────────────────────────────

export const COLORS = {
  headerBg: '#323338',
  headerFont: '#FFFFFF',
  groupBg: '#F6F7FB',
  groupFont: '#323338',
  borderColor: '#E6E9EF',
  primaryText: '#323338',
  secondaryText: '#676879',
  white: '#FFFFFF',

  // Utilization (saturated — matching the web CSS)
  off: '#ECEDF5',
  zero: '#9699A6',
  underloaded: '#66ccff',
  balanced: '#00c875',
  warning: '#fdab3d',
  overloaded: '#e2445c',

  // Status
  statusApprovedBg: '#D6F5E8',
  statusApprovedFont: '#00854d',
  statusRejectedBg: '#FBE0E4',
  statusRejectedFont: '#d83a52',
  statusSubmittedBg: '#FEF0DA',
  statusSubmittedFont: '#c67b1b',
  statusReopenedBg: '#E0ECFE',
  statusReopenedFont: '#0073ea',

  // Deviation
  deviationOver: '#d83a52',
  deviationOk: '#00a25b',
  deviationWarning: '#fdab3d',
  deviationNeutral: '#676879',

  // Non-billable (orange to match the web dot color)
  nonBillableBg: '#FEF0DA',
  nonBillableFont: '#c67b1b',

  // Leave type fallback
  holiday: '#676879',

  // Summary/total row
  totalBg: '#E8ECF2',

  // Progress (matching web bar colors)
  progressLow: '#579bfc',    // blue, <80%
  progressGood: '#00c875',   // green, >=80%
  progressOver: '#e2445c',   // red, >100%
} as const;

export const THIN_BORDER: Partial<ExcelJS.Border> = {
  style: 'thin',
  color: { argb: hexToArgb(COLORS.borderColor) },
};

export const ALL_BORDERS: Partial<ExcelJS.Borders> = {
  top: THIN_BORDER,
  bottom: THIN_BORDER,
  left: THIN_BORDER,
  right: THIN_BORDER,
};

// ─── Shared helpers ─────────────────────────────────────────────────────────

/** Create a workbook with standard properties */
export function createWorkbook(): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'WorkHub';
  wb.created = new Date();
  return wb;
}

/** Add a title row with the report name and date range */
export function addTitleRow(
  ws: ExcelJS.Worksheet,
  title: string,
  startDate: string,
  endDate: string,
): void {
  const row = ws.addRow([`${title}: ${startDate} — ${endDate}`]);
  row.font = { bold: true, size: 14, color: { argb: hexToArgb(COLORS.primaryText) } };
  row.height = 32;
  ws.addRow([]); // spacer
}

/** Style a row as a header row (dark bg, white bold text, frozen) */
export function styleHeaderRow(ws: ExcelJS.Worksheet, row: ExcelJS.Row): void {
  row.height = 28;
  row.font = { bold: true, size: 10, color: { argb: hexToArgb(COLORS.headerFont) } };
  row.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToArgb(COLORS.headerBg) },
    };
    cell.font = { bold: true, size: 10, color: { argb: hexToArgb(COLORS.headerFont) } };
    cell.border = {
      bottom: { style: 'thin', color: { argb: 'FF999999' } },
      right: { style: 'thin', color: { argb: 'FF555555' } },
    };
  });

  // Freeze pane below header
  ws.views = [{ state: 'frozen', xSplit: 0, ySplit: row.number }];
}

/** Style a row as a group header row (light bg, bold text) */
export function styleGroupRow(row: ExcelJS.Row): void {
  row.height = 24;
  row.font = { bold: true, size: 10, color: { argb: hexToArgb(COLORS.groupFont) } };
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToArgb(COLORS.groupBg) },
    };
    cell.border = ALL_BORDERS;
  });
}

/** Style a row as a summary/total row */
export function styleTotalRow(row: ExcelJS.Row): void {
  row.height = 26;
  row.font = { bold: true, size: 10, color: { argb: hexToArgb(COLORS.primaryText) } };
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToArgb(COLORS.totalBg) },
    };
    cell.border = {
      top: { style: 'medium', color: { argb: hexToArgb(COLORS.borderColor) } },
      bottom: { style: 'medium', color: { argb: hexToArgb(COLORS.borderColor) } },
      left: THIN_BORDER,
      right: THIN_BORDER,
    };
  });
}

/** Apply standard borders to all data cells in a row */
export function styleDataRow(row: ExcelJS.Row, indent = false): void {
  row.height = 22;
  row.alignment = { vertical: 'middle' };
  row.eachCell((cell) => {
    cell.border = ALL_BORDERS;
  });
  if (indent) {
    const firstCell = row.getCell(1);
    firstCell.alignment = { ...firstCell.alignment, indent: 2 };
  }
}

/** Apply a colored fill to a cell */
export function fillCell(
  cell: ExcelJS.Cell,
  bgHex: string,
  fontHex?: string,
  bold = false,
): void {
  cell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: hexToArgb(bgHex) },
  };
  if (fontHex) {
    cell.font = { ...cell.font as Partial<ExcelJS.Font>, color: { argb: hexToArgb(fontHex) }, bold };
  }
}

/** Set column widths from an array */
export function setColumnWidths(ws: ExcelJS.Worksheet, widths: number[]): void {
  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });
}

/**
 * Get utilization color matching the web CSS classes:
 * - off (weekend/holiday/leave): gray bg
 * - underloaded (1-50%): saturated blue bg, white text
 * - balanced (51-100%): saturated green bg, white text
 * - warning (101-120%): saturated orange bg, white text
 * - overloaded (>120%): saturated red bg, white text
 */
export function getUtilizationColor(rate: number, isOff = false): { bg: string; font: string } | null {
  if (isOff) return { bg: COLORS.off, font: COLORS.secondaryText };
  if (rate <= 0) return null;
  if (rate <= 50) return { bg: COLORS.underloaded, font: COLORS.white };
  if (rate <= 100) return { bg: COLORS.balanced, font: COLORS.white };
  if (rate <= 120) return { bg: COLORS.warning, font: COLORS.white };
  return { bg: COLORS.overloaded, font: COLORS.white };
}

/**
 * Get progress bar color matching the web CSS:
 * - low (<80%): blue
 * - good (80-100%): green
 * - over (>100%): red
 */
export function getProgressColor(percent: number): { bg: string; font: string } {
  if (percent > 100) return { bg: lightenHex(COLORS.progressOver, 0.5), font: COLORS.progressOver };
  if (percent >= 80) return { bg: lightenHex(COLORS.progressGood, 0.5), font: COLORS.progressGood };
  return { bg: lightenHex(COLORS.progressLow, 0.5), font: COLORS.progressLow };
}

/**
 * Get deviation color matching the web `deviationClass()` logic:
 * - Red: over budget (deviation > 0) or unplanned work (no estimate, has time spent)
 * - Green: on track (completion ratio >= 80%)
 * - Orange: under-logged (completion ratio < 80%)
 * - Gray: no data
 */
export function getDeviationColor(
  deviation: number,
  originalEstimate: number,
  timeSpent: number,
): string {
  if (originalEstimate === 0 && timeSpent === 0) return COLORS.deviationNeutral;
  if (deviation > 0) return COLORS.deviationOver;
  if (originalEstimate > 0) {
    const completionRatio = timeSpent / originalEstimate;
    if (completionRatio >= 0.8) return COLORS.deviationOk;
    return COLORS.deviationWarning;
  }
  if (timeSpent > 0) return COLORS.deviationOver;
  return COLORS.deviationNeutral;
}

/** Get approval status color matching the web UI */
export function getStatusColors(status: string | null): { bg: string; font: string } | null {
  if (!status) return null;
  switch (status) {
    case 'approved':
      return { bg: COLORS.statusApprovedBg, font: COLORS.statusApprovedFont };
    case 'rejected':
      return { bg: COLORS.statusRejectedBg, font: COLORS.statusRejectedFont };
    case 'submitted':
      return { bg: COLORS.statusSubmittedBg, font: COLORS.statusSubmittedFont };
    case 'reopened':
      return { bg: COLORS.statusReopenedBg, font: COLORS.statusReopenedFont };
    default:
      return null;
  }
}

/** Download a workbook as .xlsx */
export async function downloadWorkbook(wb: ExcelJS.Workbook, filename: string): Promise<void> {
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, filename);
}
